import {Button, Chip, Icon, IconButton, SplitButton} from '@albert-io/atomic';
import {useDraggable} from '@dnd-kit/core';
import cx from 'classnames';
import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {ItemBankRefsContext} from '../FitbItemBank/components/ItemBankRefsContext';
import AnnotatableContent from 'client/components/PracticeView/Annotations/AnnotatableContent/AnnotatableContent';
import {ANNOTATE_IGNORE_PROPS} from 'client/components/PracticeView/Annotations/engine/annotationTextProjection';
import {useIsAnnotationModeActive} from 'client/components/PracticeView/Annotations/mode/useIsAnnotationModeActive';
import {
  lockedControlProps,
  lockedGroupProps
} from 'client/components/PracticeView/Annotations/shared/annotationFreeze';
import {supplementRegexBaseString} from 'client/generic/MarkdownRendererV2/rules/supplementRules';
import supplementStore from 'client/Supplement/SupplementStore';
import supplementActions from 'client/Supplement/SupplementActions';
import {callAction, useFluxStore} from 'client/framework';
import {renderStringOrSupplement} from './utils';
import {extractSupplementParts} from './utils';
import {getDraggableClasses} from './DndContextWrapper';
import Passage from 'client/SupplementTypes/Passage/Passage';
import {ImageFullScreenModal} from 'client/SupplementTypes/Image/ImageFullScreenModal.react';
import {getCroppedImageUrl} from 'client/SupplementTypes/Image/utils';
import {
  SMALL_IMAGE_CHIP_MAX_WIDTH,
  SMALL_IMAGE_CHIP_MAX_HEIGHT
} from 'client/SupplementTypes/Image/constants';
import constants from 'client/constants';
import {SmallAudioSupplement} from './SmallAudioSupplement';
import {supplementAccessibleName} from './accessibleName';

export type DraggableChipSize = 'default' | 'small';

const SUPPLEMENT_TOKEN_REGEX = new RegExp(supplementRegexBaseString);

type SmallSupplementType = 'image' | 'audio' | 'passage';

const narrowSmallSupplement = (
  supplement: ReturnType<typeof supplementStore.getSupplement>
): {
  type: SmallSupplementType;
  supplement: ReturnType<typeof supplementStore.getSupplement>;
} | null => {
  const type = supplement.getType();
  if (type === 'image' || type === 'audio' || type === 'passage') return {type, supplement};
  return null;
};

export const getSmallSupplement = (value: string) => {
  const match = SUPPLEMENT_TOKEN_REGEX.exec(value);
  if (!match) return null;
  const id = match[1];
  if (!supplementStore.hasSupplement(id)) return null;
  return narrowSmallSupplement(supplementStore.getSupplement(id));
};

/**
 * Render-time reactive variant of getSmallSupplement: subscribes to the supplement store via the
 * bridge so the compact view appears as soon as the referenced supplement lands in the store. A bare
 * getSmallSupplement call in render is invisible to the React Compiler and would freeze once compiled.
 */
export const useSmallSupplement = (
  value: string,
  enabled: boolean
): {
  type: SmallSupplementType;
  supplement: ReturnType<typeof supplementStore.getSupplement>;
} | null => {
  const supplement = useFluxStore(supplementStore, (s) => {
    if (!enabled) return null;
    const match = SUPPLEMENT_TOKEN_REGEX.exec(value);
    if (!match) return null;
    const id = match[1];
    return s.hasSupplement(id) ? s.getSupplement(id) : null;
  });
  return useMemo(() => (supplement ? narrowSmallSupplement(supplement) : null), [supplement]);
};

/**
 * Sizing for a small-supplement chip wrapper inside a flex-wrap row (item bank, ordering answer,
 * correct-order display). A fixed-width box that neither grows nor shrinks, so removing a sibling
 * doesn't make the remaining supplements stretch to fill the freed width — they keep their size.
 */
export const SMALL_SUPPLEMENT_WRAPPER_CLASS = 'grow-0 shrink-0 basis-[200px] max-w-full min-w-x15';

/**
 * Ensures the supplement referenced by a token in `value` is fetched into the store and reports
 * when it's ready. Mirrors SupplementRenderer's fetch-on-demand so a small-supplement chip can
 * render the compact view from the first paint instead of briefly flashing the full-size
 * supplement while the supplement loads. No-ops (always ready) when `enabled` is false.
 */
export const useEnsureSupplementLoaded = (value: string, enabled: boolean) => {
  const match = enabled ? SUPPLEMENT_TOKEN_REGEX.exec(value) : null;
  const id = match ? match[1] : null;
  const [isReady, setIsReady] = useState(() => (id ? supplementStore.hasSupplement(id) : true));

  useEffect(() => {
    if (!id || supplementStore.hasSupplement(id)) {
      // oxlint-disable-next-line react/set-state-in-effect -- one-time supplement fetch/readiness check against the external supplement store
      setIsReady(true);
      return;
    }
    let cancelled = false;
    setIsReady(false);
    callAction(supplementActions.FETCH_SUPPLEMENT, id);
    Promise.resolve(supplementStore.getFetchSupplementPromise())
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  return isReady;
};

const SmallImageSupplement = ({
  supplement
}: {
  supplement: ReturnType<typeof supplementStore.getSupplement>;
}) => {
  'use memo';
  const [isFullScreen, setIsFullScreen] = useState(false);
  // small enough that the fullscreen button would cover the image, so it needs reserved space
  const [isSmallImage, setIsSmallImage] = useState(false);
  const cropValues = supplement.getCropValues?.();
  const hasCrop = cropValues && !cropValues.isEmpty?.();

  const [src, setSrc] = useState<string | null>(() => {
    if (!supplement.getImageLocation?.()?.path) return null;
    if (!hasCrop) return supplement.getImageLocation().href;
    return null;
  });

  useEffect(() => {
    if (src) return;
    if (!supplement.getImageLocation?.()?.path) return;
    if (hasCrop) {
      getCroppedImageUrl(supplement).then(setSrc);
    }
  }, [supplement, src, hasCrop]);

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const {naturalWidth, naturalHeight} = event.currentTarget;
    setIsSmallImage(
      naturalWidth < SMALL_IMAGE_CHIP_MAX_WIDTH || naturalHeight < SMALL_IMAGE_CHIP_MAX_HEIGHT
    );
  };

  if (!src) return null;
  return (
    <div
      className={cx(
        'relative flex max-h-[168px] w-full justify-center overflow-hidden',
        isSmallImage && 'pt-x5'
      )}
    >
      <img
        src={src}
        alt={supplementAccessibleName(supplement)}
        className='block min-h-0 max-w-full min-w-0 rounded object-contain'
        draggable={false}
        onLoad={handleImageLoad}
      />
      <IconButton
        color='secondary'
        variant='outlined'
        icon={['far', 'arrow-up-right-and-arrow-down-left-from-center']}
        size='s'
        label='Fullscreen'
        className='top-x1 right-x1 border-border-primary shadow-l1 absolute cursor-pointer! border **:cursor-pointer!'
        onClick={(e) => {
          e.stopPropagation();
          setIsFullScreen(true);
        }}
      />
      {isFullScreen && (
        <ImageFullScreenModal
          isMobile={false}
          caption={supplement.getCaption?.() || ''}
          setIsFullScreen={setIsFullScreen}
          supplement={supplement}
        />
      )}
    </div>
  );
};

export const SmallSupplementContent = ({
  type,
  supplement,
  isOptionEliminated = false,
  className
}: {
  type: SmallSupplementType;
  supplement: ReturnType<typeof supplementStore.getSupplement>;
  isOptionEliminated?: boolean;
  className?: string;
}) => {
  'use memo';
  if (type === 'image') return <SmallImageSupplement supplement={supplement} />;

  if (type === 'audio') {
    const audioFile = supplement.getAudio?.();
    if (!audioFile) return null;
    const src = `${constants.SUPPLEMENTS_ORIGIN}/${audioFile}`;
    const caption = supplement.getCaption?.() || supplement.getName?.() || '';
    return (
      <SmallAudioSupplement src={src} caption={caption} isOptionEliminated={isOptionEliminated} />
    );
  }

  // passage
  const content = supplement.getContent?.();
  if (!content) return null;
  return (
    <div className='w-full overflow-hidden'>
      <Passage
        content={content}
        showBorders={false}
        showHeader={false}
        forceMaxHeight
        isSmall
        className={className}
      />
    </div>
  );
};

/**
 * Renders a value that may contain a supplement token, choosing the compact supplement view when
 * the token resolves to an image/audio/passage. Ensures the referenced supplement is loaded first,
 * so the compact view shows from the first paint instead of briefly flashing the full-size
 * supplement. Use this for raw supplement content that is NOT wrapped in a draggable chip
 * (e.g. correct-answer displays, slot labels).
 */
export const SupplementContent = ({value, className}: {value: string; className?: string}) => {
  'use memo';
  const isReady = useEnsureSupplementLoaded(value, true);
  const smallSupplement = useSmallSupplement(value, true);

  if (!isReady && SUPPLEMENT_TOKEN_REGEX.test(value)) {
    return null;
  }

  if (smallSupplement) {
    return (
      <SmallSupplementContent
        type={smallSupplement.type}
        supplement={smallSupplement.supplement}
        className={className}
      />
    );
  }

  return <>{renderStringOrSupplement(value)}</>;
};

/**
 * Pure visual chip that renders a string or supplement token.
 * Use this inside DragOverlay (renderOverlay)
 *
 * When size='small' and the value is an image/audio/passage token, uses SmallSupplementContent
 *
 * Every root carries `data-annotate-ignore`: chip content never joins a host
 * annotation region's anchors (supplements are part-3's annotation surface). The
 * one exception is a text chip given `regionId`, whose text is its own region.
 */
interface SupplementChipProps {
  value: string;
  size?: DraggableChipSize;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  onRemove?: (e: React.MouseEvent) => void;
  isOptionEliminated?: boolean;
  /** When true, suppresses interactive role on the inner element (use when already wrapped by a draggable parent). */
  nested?: boolean;
  /**
   * Makes a plain text chip's text its own annotation region; other chips stay fenced. Never
   * give one to a drag ghost or fly duplicate: a second mount would clobber the chip's paint.
   */
  regionId?: string | null;
}

export const SupplementChip = ({
  value,
  size = 'default',
  className,
  onClick,
  onRemove,
  isOptionEliminated,
  nested,
  regionId = null
}: SupplementChipProps) => {
  'use memo';
  const areAnswersLocked = useIsAnnotationModeActive();
  const isSmallChip = size === 'small';
  const isSupplementReady = useEnsureSupplementLoaded(value, isSmallChip);
  const smallSupplement = useSmallSupplement(value, isSmallChip);

  // For a small-size supplement chip, defer rendering until the referenced supplement is
  // loaded — otherwise we'd briefly flash the full-size supplement (rendered via the regular
  // fallback) before switching to the compact view.
  if (isSmallChip && SUPPLEMENT_TOKEN_REGEX.test(value) && !isSupplementReady) {
    return null;
  }

  const parts = smallSupplement ? extractSupplementParts(value) : null;
  const surroundingText = parts?.text || '';

  const badgeContent = smallSupplement ? (
    <>
      {surroundingText && <span className='mr-x0-half'>{surroundingText}</span>}
      <SmallSupplementContent
        type={smallSupplement.type}
        supplement={smallSupplement.supplement}
        isOptionEliminated={isOptionEliminated}
      />
    </>
  ) : (
    renderStringOrSupplement(value)
  );

  if (onRemove && smallSupplement) {
    return (
      <div
        {...ANNOTATE_IGNORE_PROPS}
        {...(!nested && {
          role: 'button' as const,
          tabIndex: 0,
          ...lockedControlProps(areAnswersLocked)
        })}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (!nested && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onClick?.(e as any);
          }
        }}
        onClick={onClick}
        className={cx(
          'min-w-0 p-0 relative flex overflow-hidden rounded-lg border border-border-primary background-primary text-left font-normal',
          onClick ? 'cursor-grab' : 'cursor-default',
          // Audio fills the slot so replacing its caption with the scrubber on play doesn't collapse
          // the (otherwise inline, content-sized) chip and overlap the controls.
          smallSupplement.type === 'audio' && 'w-full',
          className
        )}
      >
        {badgeContent}
        <IconButton
          {...lockedControlProps(areAnswersLocked)}
          color='secondary'
          variant='outlined'
          icon='xmark'
          size='s'
          label='Remove'
          className='top-x1 left-x1 border-border-primary shadow-l1 absolute cursor-pointer! border **:cursor-pointer!'
          onClick={(e) => {
            e.stopPropagation();
            onRemove(e);
          }}
        />
      </div>
    );
  }

  if (onRemove) {
    return (
      <SplitButton
        {...ANNOTATE_IGNORE_PROPS}
        className={cx(
          'm-x0! max-w-full',
          '[&:hover>.a-button]:[--button-border-color:transparent]',
          '[&:hover>.a-button]:[--button-border-color--hover:transparent]',
          '[&:hover>.a-button]:[--button-border-color--active:transparent]',
          '[&:hover>.a-button:first-child]:[border-right:1px_solid_var(--color-border-primary)]',
          className
        )}
      >
        <Button
          {...lockedControlProps(areAnswersLocked)}
          variant='outlined'
          color='secondary'
          onClick={onClick}
          className='hover:background-transparent! min-w-0 cursor-grab overflow-hidden'
          size='s'
        >
          <span className='line-clamp-1 min-w-0'>{badgeContent}</span>
        </Button>
        <Button
          {...lockedControlProps(areAnswersLocked)}
          variant='outlined'
          color='secondary'
          onClick={(e) => {
            e.stopPropagation();
            onRemove(e);
          }}
          aria-label='Remove'
          className='hover:background-transparent! cursor-pointer! **:cursor-pointer!'
          size='s'
        >
          <Icon icon='xmark' />
        </Button>
      </SplitButton>
    );
  }

  // For small supplements, use a plain container to keep chip and supplement styles independent
  if (smallSupplement) {
    return (
      <div
        {...ANNOTATE_IGNORE_PROPS}
        {...(!nested && {
          role: 'button' as const,
          tabIndex: 0,
          ...lockedControlProps(areAnswersLocked)
        })}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (!nested && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onClick?.(e as any);
          }
        }}
        onClick={onClick}
        className={cx(
          'min-w-0 p-0 flex w-full overflow-hidden rounded-lg border border-border-primary background-primary text-left font-normal',
          onClick ? 'cursor-grab' : 'cursor-default',
          className
        )}
      >
        {badgeContent}
      </div>
    );
  }

  // The region holds the whole chip, so a drag released on its padding still marks.
  return (
    <AnnotatableContent regionId={regionId} className='contents' repaintOnMove>
      <Chip
        {...(!regionId && ANNOTATE_IGNORE_PROPS)}
        color='neutral'
        size='small'
        onClick={onClick}
        noMargin
        className={cx(
          'min-w-0 cursor-grab overflow-hidden border border-border-primary background-primary! object-contain',
          className
        )}
      >
        <span className='truncate'>{badgeContent}</span>
      </Chip>
    </AnnotatableContent>
  );
};

/**
 * Draggable chip wrapping dnd-kit's useDraggable and ItemBankRefsContext registration.
 * Use this as the actual draggable item in the question input area.
 *
 * @prop size - 'default' | 'small'. Controls chip padding/text size for compact contexts.
 */
interface DraggableChipProps {
  id: string;
  value: string;
  size?: DraggableChipSize;
  isActive?: boolean;
  isDragging?: boolean;
  activeItemId?: string | null;
  onClick?: (e: React.MouseEvent) => void;
  onRemove?: (e: React.MouseEvent) => void;
  /** When true, the chip never dims because another item is being dragged/selected. Used for items already placed in an answer. */
  suppressDimming?: boolean;
  /** See SupplementChip's `regionId`. */
  regionId?: string | null;
}

export const DraggableChip = ({
  id,
  value,
  size = 'default',
  isActive,
  isDragging,
  activeItemId,
  onClick,
  onRemove,
  suppressDimming,
  regionId
}: DraggableChipProps) => {
  'use memo';
  const isAnnotationModeActive = useIsAnnotationModeActive();
  // The shield misses events from the chip's portaled note editor; React bubbles them here.
  const {attributes, listeners, setNodeRef, transform} = useDraggable({
    id,
    disabled: isAnnotationModeActive
  });
  const {registerItemEl} = useContext(ItemBankRefsContext);

  const itemRef = useCallback(
    (el: HTMLDivElement | null) => {
      setNodeRef(el);
      registerItemEl(id, el);
    },
    [setNodeRef, registerItemEl, id]
  );

  const isDraggingThis = !!transform;
  const isAnotherItemActive = activeItemId !== null && !isActive;

  let shouldShowReducedOpacity = false;
  if (isDraggingThis || suppressDimming) {
    shouldShowReducedOpacity = false;
  } else if (isDragging || isAnotherItemActive) {
    shouldShowReducedOpacity = true;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.(e);
  };

  const isSupplement = !!extractSupplementParts(value);
  let draggableClasses = getDraggableClasses(
    isDraggingThis,
    isAnnotationModeActive,
    !!regionId && !isSupplement
  );
  if (isSupplement) {
    draggableClasses = draggableClasses.replace('hover:background-subtle-hover!', '');
  }

  return (
    <div
      ref={itemRef}
      {...attributes}
      {...listeners}
      {...lockedGroupProps(isAnnotationModeActive, attributes['aria-describedby'])}
      data-item-id={id}
    >
      <SupplementChip
        value={value}
        size={size}
        onClick={handleClick}
        onRemove={onRemove}
        nested
        regionId={regionId}
        className={cx(draggableClasses, {
          // A rotated chip becomes the containing block for its note editor's fixed anchor.
          '-rotate-6': isActive && !isAnnotationModeActive,
          'background-subtle-hover!': isActive,
          'opacity-20': isDraggingThis,
          'opacity-50': shouldShowReducedOpacity,
          'z-10 shadow-l1': isActive || isDragging
        })}
      />
    </div>
  );
};
